import React, { useState, useMemo, useEffect } from "react";
import { mediaPosts, mediaCategories } from "../constant/media";
import MediaPostCard from "./MediaPostCard";
import FilterPills from "./FilterPills";
import "./MediaComp.css";

const MediaComp = () => {
  const [selectedCategory, setSelectedCategory] = useState("All Posts");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeModalPost, setActiveModalPost] = useState(null);
  const [toastMessage, setToastMessage] = useState("");

  // Set page title, SEO metadata and refresh AOS on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Media & Social | Tedekstra Digital Innovation & Engineering Insights";
    let metaTag = document.querySelector("meta[name='description']");
    const description =
      "Explore the latest company updates, engineering insights, SaaS innovations, and client spotlights directly from Tedekstra's official LinkedIn.";
    if (metaTag) {
      metaTag.setAttribute("content", description);
    } else {
      metaTag = document.createElement("meta");
      metaTag.name = "description";
      metaTag.content = description;
      document.head.appendChild(metaTag);
    }

    if (window.AOS) {
      window.AOS.refresh();
    }
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (activeModalPost) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [activeModalPost]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && activeModalPost) {
        setActiveModalPost(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeModalPost]);

  // Filtered posts based on category and search query
  const filteredPosts = useMemo(() => {
    return mediaPosts.filter((post) => {
      const matchesCategory =
        selectedCategory === "All Posts" || post.category === selectedCategory;

      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        q === "" ||
        post.title.toLowerCase().includes(q) ||
        post.excerpt.toLowerCase().includes(q) ||
        (post.tags && post.tags.some((t) => t.toLowerCase().includes(q)));

      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  // Copy link share handler
  const handleShare = async (post) => {
    const shareUrl = post.postUrl;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        showToast("Link copied to clipboard!");
        return;
      } catch (err) {
        // fallback
      }
    }
    // Fallback prompt
    showToast("Post URL copied!");
  };

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage("");
    }, 2800);
  };

  return (
    <div className="media-page-wrapper">
      {/* 1. Hero Header Section */}
      <section className="media-hero-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-md-10 col-lg-8">
              <div className="media-badge-pill">
                <i className="fa-brands fa-linkedin"></i>
                <span>IN MEDIA &amp; SOCIAL</span>
              </div>
              <h1 className="media-main-title">
                Discover Us in Action
              </h1>
              <p className="media-main-subtitle">
                Explore our latest company updates, engineering insights, industry spotlights, and thought leadership direct from LinkedIn.
              </p>
              <div className="media-hero-linkedin-accent">
                <a
                  href="https://www.linkedin.com/company/tedekstra/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="media-hero-linkedin-link"
                  title="Visit Tedekstra on LinkedIn"
                >
                  <i className="fa-brands fa-linkedin"></i>
                  <span>Follow @Tedekstra on LinkedIn</span>
                  <i className="fa-solid fa-arrow-up-right-from-square media-external-icon"></i>
                </a>
              </div>
            </div>
          </div>

          {/* 2. Filter Bar & Search Input */}
          <div className="media-controls-container">
            {/* Category Pills (3 on top row, 2 on bottom row) */}
            <FilterPills
              categories={mediaCategories}
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
              splitConfig={[3, 2]}
            />

            {/* Search Input */}
            <div className="media-search-wrapper">
              <i className="fa-solid fa-magnifying-glass media-search-icon"></i>
              <input
                type="text"
                className="media-search-input"
                placeholder="Search posts by topic, keyword, or hashtag..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="media-search-clear"
                  onClick={() => setSearchQuery("")}
                  title="Clear search"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 3. Media Posts Uniform Grid */}
      <div className="container">
        {filteredPosts.length > 0 ? (
          <div className="media-uniform-grid">
            {filteredPosts.map((post) => (
              <MediaPostCard
                key={post.id}
                post={post}
                onShare={handleShare}
              />
            ))}
          </div>
        ) : (
          /* Empty / No Results State */
          <div className="media-empty-state">
            <div className="media-empty-icon">
              <i className="fa-regular fa-newspaper"></i>
            </div>
            <h4 className="media-empty-title">No posts found</h4>
            <p className="media-empty-text">
              We couldn't find any posts matching "{searchQuery}". Try searching for something else or reset the filter.
            </p>
            <button
              type="button"
              className="media-btn-reset"
              onClick={() => {
                setSelectedCategory("All Posts");
                setSearchQuery("");
              }}
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>

      {/* 4. Interactive Live LinkedIn Modal */}
      {activeModalPost && (
        <div
          className="media-modal-backdrop"
          onClick={() => setActiveModalPost(null)}
        >
          <div
            className="media-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="media-modal-header">
              <h5 className="media-modal-title">
                <i className="fa-brands fa-linkedin text-primary"></i>
                <span>Live LinkedIn Post</span>
              </h5>
              <button
                type="button"
                className="media-modal-close-btn"
                onClick={() => setActiveModalPost(null)}
                aria-label="Close modal"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <div className="media-modal-body">
              <iframe
                src={activeModalPost.embedUrl}
                title={activeModalPost.title}
                className="media-modal-iframe"
                frameBorder="0"
                allowFullScreen=""
              ></iframe>
            </div>

            <div className="media-modal-footer">
              <span className="media-modal-info">
                {activeModalPost.date} &bull; {activeModalPost.category}
              </span>
              <a
                href={activeModalPost.postUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="media-btn-linkedin"
                style={{ flexGrow: "unset", padding: "6px 16px" }}
              >
                <i className="fa-brands fa-linkedin"></i>
                <span>Open in LinkedIn</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* 5. Toast Alert */}
      {toastMessage && (
        <div className="media-toast-notification">
          <i className="fa-solid fa-circle-check text-success"></i>
          <span>{toastMessage}</span>
        </div>
      )}
    </div>
  );
};

export default MediaComp;
